import { useState, useEffect } from "react";
import api from "../api";

const SHOW_DASHBOARD_KEY = "student-show-dashboard";
const USE_MOBILE_DASHBOARD_KEY = "student-use-mobile-dashboard";
const TESTER_VIEW_KEY = "student-dashboard-tester-view";
const SUBSCRIPTION_LOCK_KEY = "student-subscription-lock";
const CAN_TOGGLE_KEY = "student-can-toggle-dashboard";
const HAS_COURSE_KEY = "student-has-course-enrollment";
/**
 * Per account: one browser is often shared by siblings, and a "no course" answer cached for one
 * child must never hide Homework, Video and Classes from the next one while settings load.
 */
const hasCourseKey = () => {
  try {
    const id = JSON.parse(localStorage.getItem("userData") || "{}")?.id;
    return id ? `${HAS_COURSE_KEY}:${id}` : HAS_COURSE_KEY;
  } catch {
    return HAS_COURSE_KEY;
  }
};
// Read by api.js and sent as X-Student-Context, so the API answers for the chosen side.
export const DASHBOARD_VIEW_KEY = "student-dashboard-view";
const VIEW_CHANGED_EVENT = "student-tester-view-changed";

const readCachedLock = () => {
  try {
    const raw = localStorage.getItem(SUBSCRIPTION_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const lockFromSettingsPayload = (d) => {
  if (d?.subscription_locked === true) {
    return {
      locked: true,
      // Older APIs omit this: fall back to the plan lock, the previous behaviour.
      worksheetsLocked: d.subscription_worksheets_locked !== false,
      type: d.subscription_notification?.type || "",
      message: d.subscription_notification?.message || "",
      portalAvailable: d.subscription_billing?.stripe_portal_available === true,
      checkoutAvailable: d.subscription_billing?.stripe_checkout_available !== false,
      // "payment_method_update" means their card failed: they already own the plan, so the
      // gate shows the card form alone instead of a plan picker at today's prices.
      recoveryAction: d.subscription_billing?.recovery_action || "choose_plan",
      retainedPrice: d.subscription_billing?.retained_price || null,
      preferredPlanId: Number(d.preferred_plan_id || 0) || null,
      // "items" | "plan" once they answered the choose-plan question for this lapse.
      lapsedChoice: d.lapsed_plan_choice || null,
      isTrial: d.subscription_is_trial === true,
      trialEndsAt: d.subscription_trial_ends_at || null,
      // Free trial ended with no plan: only the plan screen and items already bought open.
      catalogueLocked: d.subscription_catalogue_locked === true,
      // Flash cards + games, and the wizard. An ended trial that bought a mock exam, past or
      // practice paper keeps them. Older APIs omit these: fall back to the previous rules.
      extrasLocked: d.subscription_extras_locked ?? d.subscription_worksheets_locked !== false,
      wizardLocked: d.subscription_wizard_locked ?? true,
    };
  }
  // A running free trial is unlocked; the dashboard still says when it ends.
  return {
    locked: false,
    isTrial: d?.subscription_is_trial === true,
    trialEndsAt: d?.subscription_trial_ends_at || null,
  };
};

const lockFromAccessPayload = (d) => {
  if (d?.locked === true) {
    return {
      locked: true,
      worksheetsLocked: d.worksheets_locked !== false,
      type: d.notification?.type || "",
      message: d.notification?.message || "",
      portalAvailable: d.billing?.stripe_portal_available === true,
      checkoutAvailable: d.billing?.stripe_checkout_available !== false,
      recoveryAction: d.billing?.recovery_action || "choose_plan",
      retainedPrice: d.billing?.retained_price || null,
      preferredPlanId: Number(d.preferred_plan_id || 0) || null,
      isTrial: d.is_trial === true,
      trialEndsAt: d.trial_ends_at || null,
      catalogueLocked: d.catalogue_locked === true,
      extrasLocked: d.extras_locked ?? d.worksheets_locked !== false,
      wizardLocked: d.wizard_locked ?? true,
    };
  }
  return {
    locked: false,
    isTrial: d?.is_trial === true,
    trialEndsAt: d?.trial_ends_at || null,
  };
};

/**
 * One `student/settings` request shared by every consumer of this hook.
 *
 * The hook is mounted several times on a single page — the layout, the dashboard and the
 * route guards each call it — and each mount used to fire its own request. That payload is
 * one of the most expensive the API serves (it reconciles the Stripe subscription), so a
 * dashboard load was paying for it three times over, in parallel, for an identical answer.
 *
 * Concurrent callers share the in-flight promise; callers shortly after share the result.
 * The TTL is deliberately short: this is about collapsing one page's burst of identical
 * requests, not about caching state across a session.
 */
const SETTINGS_TTL_MS = 30000;
let settingsInFlight = null;
let settingsCached = null;
let settingsCachedAt = 0;

/** Drop the shared payload so the next mount refetches (after a plan purchase, say). */
export const refreshStudentPortalMode = () => {
  settingsInFlight = null;
  settingsCached = null;
  settingsCachedAt = 0;
};

/**
 * @returns {Promise<{source: "settings"|"access", data: object}>}
 * Resolves with whichever endpoint answered, so callers map the payload themselves.
 */
const loadPortalSettings = () => {
  if (settingsInFlight) return settingsInFlight;
  if (settingsCached && Date.now() - settingsCachedAt < SETTINGS_TTL_MS) {
    return Promise.resolve(settingsCached);
  }

  settingsInFlight = api
    .get("student/settings")
    .then((res) => ({ source: "settings", data: res.data?.data ?? {} }))
    // Older APIs may not serve student/settings; the lock alone still gates the portal.
    .catch(() =>
      api
        .get("student/subscription/access")
        .then((res) => ({ source: "access", data: res.data?.data ?? {} }))
    )
    .then((result) => {
      settingsCached = result;
      settingsCachedAt = Date.now();
      return result;
    })
    .finally(() => {
      settingsInFlight = null;
    });

  return settingsInFlight;
};

export const setTesterDashboardView = (view) => {
  localStorage.setItem(TESTER_VIEW_KEY, view === "mobile" ? "mobile" : "course");
  window.dispatchEvent(new CustomEvent(VIEW_CHANGED_EVENT));
};

/**
 * A student with both a course and a mobile plan switches sides here. Each side keeps its
 * own lists and scores on the API (App\Support\StudentAccessContext).
 */
export const setDashboardView = (view) => {
  localStorage.setItem(DASHBOARD_VIEW_KEY, view === "mobile" ? "mobile" : "course");
  window.dispatchEvent(new CustomEvent(VIEW_CHANGED_EVENT));
};

const readDashboardView = () =>
  localStorage.getItem(DASHBOARD_VIEW_KEY) === "mobile" ? "mobile" : "course";

/**
 * Student portal shape: whether the Dashboard tab is on (Settings → Student) and whether this
 * student gets the mobile-subscription dashboard instead of the course one. Cached in
 * localStorage so the sidebar and the dashboard paint correctly before the request lands.
 */
export default function useStudentPortalMode() {
  const isTester = (localStorage.getItem("role") || "").toLowerCase() === "tester";
  const [showDashboard, setShowDashboard] = useState(
    localStorage.getItem(SHOW_DASHBOARD_KEY) !== "0"
  );
  const [useMobileDashboard, setUseMobileDashboard] = useState(
    localStorage.getItem(USE_MOBILE_DASHBOARD_KEY) === "1"
  );
  const [testerView, setTesterView] = useState(
    localStorage.getItem(TESTER_VIEW_KEY) === "mobile" ? "mobile" : "course"
  );
  const [canToggleDashboard, setCanToggleDashboard] = useState(
    localStorage.getItem(CAN_TOGGLE_KEY) === "1"
  );
  const [dashboardView, setDashboardViewState] = useState(readDashboardView);
  // Unknown counts as enrolled, so an older API (or a failed lookup) keeps today's sidebar.
  const [hasCourseEnrollment, setHasCourseEnrollment] = useState(
    localStorage.getItem(hasCourseKey()) !== "0"
  );
  const [subscriptionLock, setSubscriptionLock] = useState(() => readCachedLock());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sync = () => {
      setTesterView(localStorage.getItem(TESTER_VIEW_KEY) === "mobile" ? "mobile" : "course");
      setDashboardViewState(readDashboardView());
    };
    window.addEventListener(VIEW_CHANGED_EVENT, sync);
    return () => window.removeEventListener(VIEW_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    if (isTester) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const applyLock = (lock) => {
      localStorage.setItem(SUBSCRIPTION_LOCK_KEY, JSON.stringify(lock));
      setSubscriptionLock(lock);
    };

    // A failed lookup must never shut the student out: the portal is open either way.
    const softFailLock = () => applyLock({ locked: false });

    loadPortalSettings()
      .then(({ source, data }) => {
        if (cancelled) return;
        if (source === "access") {
          applyLock(lockFromAccessPayload(data));
          return;
        }
        const dashboardOn = data.show_dashboard !== false;
        const mobile = data.use_mobile_dashboard === true;
        const canToggle = data.can_toggle_dashboard === true;
        localStorage.setItem(SHOW_DASHBOARD_KEY, dashboardOn ? "1" : "0");
        localStorage.setItem(USE_MOBILE_DASHBOARD_KEY, mobile ? "1" : "0");
        localStorage.setItem(CAN_TOGGLE_KEY, canToggle ? "1" : "0");
        const enrolled = data.has_course_enrollment !== false;
        localStorage.setItem(hasCourseKey(), enrolled ? "1" : "0");
        // No longer has both: stop asking the API for a side (it would ignore it anyway).
        if (!canToggle && localStorage.getItem(DASHBOARD_VIEW_KEY) !== null) {
          localStorage.removeItem(DASHBOARD_VIEW_KEY);
          setDashboardViewState("course");
        }
        // Has both but never picked: store the Course side the toggle shows, so every request
        // says which side it is for.
        if (canToggle && localStorage.getItem(DASHBOARD_VIEW_KEY) === null) {
          localStorage.setItem(DASHBOARD_VIEW_KEY, "course");
          setDashboardViewState("course");
        }
        setShowDashboard(dashboardOn);
        setUseMobileDashboard(mobile);
        setCanToggleDashboard(canToggle);
        setHasCourseEnrollment(enrolled);
        applyLock(lockFromSettingsPayload(data));
      })
      .catch(() => {
        if (!cancelled) softFailLock();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTester]);

  // Testers hold an Admin-scope token, so student/settings cannot answer for them. They pick the
  // view by hand and keep the full sidebar so both dashboards stay reachable.
  // A student with a course and a plan picks a side; the sidebar follows it like any other
  // mobile or course student's.
  const canToggle = !isTester && canToggleDashboard;
  const mobileDashboard = isTester
    ? testerView === "mobile"
    : canToggle
      ? dashboardView === "mobile"
      : useMobileDashboard;
  return {
    showDashboard: isTester ? true : showDashboard,
    useMobileDashboard: mobileDashboard,
    // A new web student with no course has no homework, classes or videos to show either.
    hideCourseOnlySections: !isTester && (mobileDashboard || !hasCourseEnrollment),
    hasCourseEnrollment: isTester ? true : hasCourseEnrollment,
    // Course-enrolled students use Homework for assigned resources — hide the mobile worksheet catalog.
    hideMobileOnlySections: !isTester && !mobileDashboard,
    isTester,
    testerView,
    canToggleDashboard: canToggle,
    dashboardView: isTester ? testerView : canToggle ? dashboardView : mobileDashboard ? "mobile" : "course",
    // Gates the app-plan sections (Work Sheet, Wizard, Flash Cards, Games) for app-side
    // accounts without a running plan or free trial — the backend decides, via
    // MobileSubscriptionAccessService::appFeaturesLockedFor, and sends it as `locked`.
    // A course-enrolled student is never locked. Mock exams and past papers stay open after a
    // lapsed PAID plan; after an ended free trial `catalogueLocked` closes them too.
    subscriptionLock: isTester ? { locked: false } : (subscriptionLock || { locked: false }),
    // Plan ended AND nothing ever bought, or a free trial ended: every tab is locked and only
    // the plan screen opens. `worksheetsLocked` carries the answer from
    // MobileSubscriptionAccessService::worksheetsLockedFor().
    planFullyLocked:
      !isTester &&
      subscriptionLock?.locked === true &&
      (subscriptionLock?.worksheetsLocked === true || subscriptionLock?.catalogueLocked === true),
    // Free trial ended with no plan: no previews, no one-off purchases, plan screen only.
    catalogueLocked: !isTester && subscriptionLock?.locked === true && subscriptionLock?.catalogueLocked === true,
    // Flash cards + games, and the wizard (MobileSubscriptionAccessService::extrasLockedFor /
    // wizardLockedFor). Only meaningful while `locked`; an unlocked account has them all.
    extrasLocked: !isTester && subscriptionLock?.locked === true && subscriptionLock?.extrasLocked !== false,
    wizardLocked: !isTester && subscriptionLock?.locked === true && subscriptionLock?.wizardLocked !== false,
    subscriptionInfo: isTester ? { locked: false } : (subscriptionLock || { locked: false }),
    subscriptionLockResolved: true,
    // False only on a first load with nothing cached (right after login, since sign-out
    // clears storage): `subscriptionLock` then reads "unlocked" until the request lands.
    subscriptionLockKnown: isTester || !loading || subscriptionLock !== null,
    loading: isTester ? false : loading,
  };
}
